import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../../../shared/components/Button.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { useIncrementalList } from "../../../shared/hooks/useIncrementalList.js";
import { ListSentinel } from "../../../shared/components/ListSentinel.jsx";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { useCompanies } from "../../companies/hooks/useCompanies.js";
import { getBankAccounts, maskAccountNumber } from "../../companies/models/company.js";
import { financialYearRange, weekRange, monthRange } from "../../../shared/utils/dateRanges.js";
import { DateField } from "../../../shared/components/DateField.jsx";
import { getAccountTransactions, getAccountImports, deleteImport } from "../services/bankService.js";
import { StatementImportSheet } from "../components/StatementImportSheet.jsx";
import { MatchQueue } from "../components/MatchQueue.jsx";
import { bankTxnColumns, matchLabel } from "../components/bankTxnColumns.jsx";
import { useIsDesktop } from "../../../shared/hooks/useMediaQuery.js";
import { DataTable } from "../../../shared/components/DataTable.jsx";

const RANGES = [
  { key: "thisWeek", label: "This week" },
  { key: "thisMonth", label: "This month" },
  { key: "lastMonth", label: "Last month" },
  { key: "thisFY", label: "This FY" },
  { key: "all", label: "All" },
  { key: "custom", label: "📅 Custom" }
];
const RANGE_KEYS = RANGES.map((r) => r.key);
const rupees = (paise) => `₹${(Number(paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Bank accounts of each company and their imported statement transactions.
 * Data loads only on this page: one query per account + date range, cached for the session.
 */
export function BankingPage() {
  const { companies, defaultCompany, refetch: refetchCompanies } = useCompanies();
  const { confirmDanger } = useDangerConfirm();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [txns, setTxns] = useState([]);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const cache = useRef(new Map());
  const [reloadTick, setReloadTick] = useState(0);
  // Opens on "To match" for "This week"; tab and range live in the URL so reload / Back keep them
  const tab = ["txns", "imports"].includes(params.get("tab")) ? params.get("tab") : "match";
  const rangeKey = RANGE_KEYS.includes(params.get("range")) ? params.get("range") : "thisWeek";

  const companyId = params.get("company") || defaultCompany?.id || companies[0]?.id || "";
  const company = companies.find((c) => c.id === companyId) || null;
  const accounts = useMemo(() => getBankAccounts(company), [company]);
  const accountId = params.get("account") && accounts.some((a) => a.id === params.get("account"))
    ? params.get("account")
    : (accounts.find((a) => a.isPrimary) || accounts[0])?.id || "";
  const account = accounts.find((a) => a.id === accountId) || null;

  const select = (next) => setParams((prev) => {
    const p = new URLSearchParams(prev);
    Object.entries(next).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    return p;
  }, { replace: true });

  const customFrom = params.get("from") || "";
  const customTo = params.get("to") || "";
  const { from, to } = useMemo(() => {
    if (rangeKey === "thisWeek") return weekRange();
    if (rangeKey === "thisMonth") return monthRange(0);
    if (rangeKey === "lastMonth") return monthRange(-1);
    if (rangeKey === "thisFY") return financialYearRange(0);
    if (rangeKey === "all") return { from: "", to: "" };
    const fallback = monthRange(0);
    return { from: customFrom || fallback.from, to: customTo || fallback.to };
  }, [rangeKey, customFrom, customTo]);
  const setRange = (key, custom = null) => select({
    range: key === "thisWeek" ? "" : key,
    from: key === "custom" ? (custom?.from ?? from) : "",
    to: key === "custom" ? (custom?.to ?? to) : ""
  });

  const load = useCallback(async ({ force = false } = {}) => {
    if (!accountId) {
      setTxns([]);
      setImports([]);
      return;
    }
    const key = `${accountId}|${from}|${to}`;
    if (!force && cache.current.has(key)) {
      setTxns(cache.current.get(key));
    } else {
      setLoading(true);
      try {
        const list = await getAccountTransactions(accountId, { from, to });
        cache.current.set(key, list);
        setTxns(list);
      } catch (err) {
        console.error("Failed to load bank transactions:", err);
      } finally {
        setLoading(false);
      }
    }
    getAccountImports(accountId).then(setImports).catch(() => setImports([]));
  }, [accountId, from, to]);

  useEffect(() => {
    load({ force: reloadTick > 0 });
  }, [load, reloadTick]);

  // A line was matched / unmatched: update it in place (and in the cached ranges) without reloading
  const onTxnUpdated = useCallback((id, patch) => {
    const update = (list) => list.map((t) => (t.id === id ? { ...t, ...patch } : t));
    setTxns(update);
    cache.current.forEach((list, key) => cache.current.set(key, update(list)));
  }, []);
  const unmatchedCount = useMemo(() => txns.filter((t) => t.matchStatus !== "matched").length, [txns]);

  const refreshAfterChange = async () => {
    cache.current.clear();
    await load({ force: true });
  };

  const shown = useMemo(() => {
    const term = search.toLowerCase().trim();
    const list = term
      ? txns.filter((t) => `${t.description} ${t.ref}`.toLowerCase().includes(term) || String(t.debitPaise / 100).includes(term) || String(t.creditPaise / 100).includes(term))
      : txns;
    return [...list].reverse(); // newest first
  }, [txns, search]);
  const totals = useMemo(() => shown.reduce((s, t) => ({ in: s.in + (t.creditPaise || 0), out: s.out + (t.debitPaise || 0) }), { in: 0, out: 0 }), [shown]);
  const list = useIncrementalList(shown, { resetKey: `${accountId}|${from}|${to}|${search}` });

  const dateFormat = company?.dateFormat || defaultCompany?.dateFormat || "DD-MM-YYYY";
  const fmt = useCallback((d) => formatAppDate(d, dateFormat), [dateFormat]);
  const isDesktop = useIsDesktop();
  const txnColumns = useMemo(() => bankTxnColumns({ fmt, rupees }), [fmt]);

  const undoImport = async (imp) => {
    const ok = await confirmDanger({
      title: `Undo import of ${imp.fileName}?`,
      message: `This removes the ${imp.added} transaction(s) this import added to ${account?.bankName || "the account"} ${maskAccountNumber(account?.accountNumber)}.\n\nRows that later statements also contained were saved by this import and will be removed too — re-import those files to restore them.`,
      confirmLabel: "Undo import"
    });
    if (!ok) return;
    try {
      await deleteImport(imp);
    } catch (err) {
      alert(err.message || "Could not undo the import.");
      return;
    }
    await refreshAfterChange();
  };

  return (
    <div className="banking-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 700 }}>🏦 Bank Accounts</h2>
          <p style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>Statements are read on this device; only the transactions are saved.</p>
        </div>
        <Button size="sm" variant="primary" onClick={() => setImportOpen(true)} disabled={!companies.length}>⬆️ Import statement PDF</Button>
      </div>

      {companies.length > 1 && (
        <div className="filter-pills-row company-pills">
          {companies.map((c) => (
            <button key={c.id} type="button" className={`filter-pill ${c.id === companyId ? "active" : ""}`} onClick={() => select({ company: c.id, account: "" })}>
              {c.name}
            </button>
          ))}
        </div>
      )}

      {accounts.length === 0 ? (
        <Card style={{ padding: "1.25rem", textAlign: "center", color: "var(--text-dim)" }}>
          No bank accounts for {company?.name || "this company"} yet. Add them in 🏢 Companies → edit company → Bank Accounts, or import a statement and choose "Add as a new account".
        </Card>
      ) : (
        <div style={{ display: "flex", gap: "0.5rem", overflowX: "auto", paddingBottom: "4px", marginBottom: "0.75rem" }}>
          {accounts.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => select({ company: companyId, account: a.id })}
              style={{ flexShrink: 0, textAlign: "left", padding: "0.6rem 0.8rem", borderRadius: "var(--radius-md)", cursor: "pointer", background: "var(--bg-card)", color: "var(--text-main)", border: a.id === accountId ? "1.5px solid var(--primary)" : "1px solid var(--border-subtle)" }}
            >
              <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>{a.bankName} {maskAccountNumber(a.accountNumber)} {a.isPrimary && <span style={{ fontSize: "0.7rem", color: "var(--primary)" }}>★</span>}</div>
              <div style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>{[a.branchName, a.ifscCode].filter(Boolean).join(" • ")}</div>
            </button>
          ))}
        </div>
      )}

      {account && (
        <>
          <div className="filter-pills-row" style={{ marginBottom: "0.5rem", flexWrap: "wrap", overflowX: "visible" }}>
            {RANGES.map((r) => (
              <button key={r.key} type="button" className={`filter-pill ${rangeKey === r.key ? "active" : ""}`} onClick={() => setRange(r.key)}>{r.label}</button>
            ))}
          </div>
          {rangeKey === "custom" && (
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <DateField label="From" value={from} max={to} dateFormat={dateFormat} onChange={(v) => setRange("custom", { from: v, to: v > to ? v : to })} />
              <DateField label="To" value={to} min={from} dateFormat={dateFormat} onChange={(v) => setRange("custom", { from: v < from ? v : from, to: v })} />
            </div>
          )}
          <div className="filter-pills-row bank-tabs" style={{ marginBottom: "0.5rem" }}>
            {[["txns", "Transactions"], ["match", `To match${unmatchedCount ? ` (${unmatchedCount})` : ""}`], ["imports", "Imports"]].map(([key, label]) => (
              <button key={key} type="button" className={`filter-pill ${tab === key ? "active" : ""}`} onClick={() => select({ tab: key === "match" ? "" : key })}>{label}</button>
            ))}
          </div>

          {tab === "match" && (loading
            ? <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1.5rem" }}>⏳ Loading transactions…</div>
            : <MatchQueue key={`${accountId}|${from}|${to}`} txns={txns} company={company} companies={companies} account={account} fmt={fmt} onTxnUpdated={onTxnUpdated}
                rangeLabel={RANGES.find((r) => r.key === rangeKey)?.label.replace("📅 ", "") || ""}
                widerRanges={rangeKey === "all" ? [] : RANGES.filter((r) => ["thisMonth", "lastMonth", "all"].includes(r.key) && r.key !== rangeKey)}
                onWidenRange={(key) => setRange(key)} />)}

          {tab === "txns" && (<>
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input type="text" className="search-input" placeholder="Search description, cheque / UTR, amount…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          <div className="metrics-row">
            <div className="metric-card"><div className="metric-label">Money in</div><div className="metric-value" style={{ color: "var(--success)" }}>{rupees(totals.in)}</div></div>
            <div className="metric-card"><div className="metric-label">Money out</div><div className="metric-value" style={{ color: "var(--danger)" }}>{rupees(totals.out)}</div></div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", color: "var(--text-dim)", padding: "1.5rem" }}>⏳ Loading transactions…</div>
          ) : shown.length === 0 ? (
            <Card style={{ padding: "1.25rem", textAlign: "center", color: "var(--text-dim)" }}>
              {txns.length ? "No transactions match your search." : "No transactions in this period. Import a statement PDF to add them."}
            </Card>
          ) : isDesktop ? (
            <DataTable
              columns={txnColumns}
              rows={shown}
              storageKey="bank_txns"
              noun="transactions"
              resetKey={`${accountId}|${from}|${to}|${search}`}
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {list.visibleItems.map((t) => (
                <div key={t.id} style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderLeft: `3px solid ${t.creditPaise ? "var(--success)" : "var(--danger)"}`, borderRadius: "var(--radius-md)", padding: "0.55rem 0.75rem" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "0.82rem", color: "var(--text-main)", wordBreak: "break-word" }}>{t.description || "—"}</div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>📅 {fmt(t.date)}{t.time ? ` ${t.time.slice(0, 5)}` : ""}{t.ref ? ` • Ref ${t.ref}` : ""}</div>
                    {t.matchStatus === "matched" && (
                      <span style={{ display: "inline-block", marginTop: 3, fontSize: "0.7rem", fontWeight: 600, padding: "1px 7px", borderRadius: 10, background: "var(--success-bg, rgba(16,185,129,.12))", color: "var(--success)" }}>
                        {matchLabel(t)}
                      </span>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, color: t.creditPaise ? "var(--success)" : "var(--danger)" }}>
                      {t.creditPaise ? `+${rupees(t.creditPaise)}` : `−${rupees(t.debitPaise)}`}
                    </div>
                    {t.balancePaise !== null && t.balancePaise !== undefined && (
                      <div style={{ fontSize: "0.7rem", color: "var(--text-dim)" }}>Bal {rupees(t.balancePaise)}</div>
                    )}
                  </div>
                </div>
              ))}
              <ListSentinel sentinelRef={list.sentinelRef} hasMore={list.hasMore} visibleCount={list.visibleCount} total={list.total} onShowMore={list.showMore} noun="transactions" />
            </div>
          )}
          </>)}

          {tab === "imports" && (
          <div>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "0.4rem" }}>Imported statements</div>
            {imports.length === 0 ? (
              <div style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>None yet.</div>
            ) : imports.map((imp) => (
              <div key={imp.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", padding: "0.45rem 0", borderBottom: "1px solid var(--border-subtle)", fontSize: "0.8rem" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, wordBreak: "break-all" }}>📄 {imp.fileName}</div>
                  <div style={{ color: "var(--text-dim)" }}>{fmt(imp.periodFrom)} → {fmt(imp.periodTo)} • {imp.added} added{imp.duplicates ? `, ${imp.duplicates} skipped` : ""} • {imp.importedBy}</div>
                </div>
                <button type="button" className="btn btn-secondary btn-sm" style={{ color: "var(--danger)", fontSize: "0.72rem", padding: "2px 8px", flexShrink: 0 }} onClick={() => undoImport(imp)}>🗑 Undo</button>
              </div>
            ))}
          </div>
          )}
        </>
      )}

      <StatementImportSheet
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        companies={companies}
        dateFormat={dateFormat}
        refetchCompanies={refetchCompanies}
        onImported={(target, period) => {
          cache.current.clear();
          // Show the statement's own period on "To match", so the new lines are right there
          select({ company: target.company.id, account: target.account.id, tab: "", range: "custom", from: period?.from || "", to: period?.to || "" });
          setReloadTick((n) => n + 1); // reload once the new selection is in place
        }}
      />
    </div>
  );
}

export default BankingPage;
